<template>
    <div class="optimize-open-content">
        <div class="container my-5">
            <form @submit.prevent="optimize">
                <DataInput :title="rt(tm('optimizeOpen.demandData'))" id="demandFile" v-model="demandData"
                    @change="event => handleFileChange(event, 'demand')" />
                <p v-if="demandError" class="text-danger">{{ demandError }}</p>
                <DataInput :title="rt(tm('optimizeOpen.productMaster'))" id="productMasterFile"
                    v-model="productMasterData" @change="event => handleFileChange(event, 'productMaster')" />
                <p v-if="productMasterError" class="text-danger">{{ productMasterError }}</p>
                <DataInput :title="rt(tm('optimizeOpen.resourceMaster'))" id="machineResourcesFile"
                    v-model="machineResourcesData" @change="event => handleFileChange(event, 'machineResources')" />
                <p v-if="machineResourcesError" class="text-danger">{{ machineResourcesError }}</p>
                <div class="text-center mt-4">
                    <button type="submit" class="btn btn-primary btn-lg" :disabled="isFormInvalid || isCalculating">
                        {{ isCalculating ? rt(tm('optimizeOpen.calculating')) : rt(tm('optimizeOpen.optimize')) }}
                    </button>
                </div>
            </form>
            <div v-if="isCalculating" class="text-center mt-3">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">{{ rt(tm('optimizeOpen.calculating')) }}</span>
                </div>
                <p class="mt-2">{{ rt(tm('optimizeOpen.calculatingMessage')) }}</p>
            </div>
            <ResultDisplay :result="optimizationResult" :demandData="demandData"
                :machineResourcesData="machineResourcesData" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DataInput from '../components/componentsDetail/DataInput.vue';
import ResultDisplay from '../components/componentsDetail/ResultDisplay.vue';
import { getAnalytics, logEvent } from "firebase/analytics";

const { tm, rt } = useI18n();

const demandData = ref(rt(tm('optimizeOpen.defaultDemandData')));
const productMasterData = ref(rt(tm('optimizeOpen.defaultProductMasterData')));
const machineResourcesData = ref(rt(tm('optimizeOpen.defaultMachineResourcesData')));
const optimizationResult = ref(null);

const demandError = ref('');
const productMasterError = ref('');
const machineResourcesError = ref('');
const isCalculating = ref(false);

const isFormInvalid = computed(() => {
    return demandError.value !== '' || productMasterError.value !== '' || machineResourcesError.value !== '' || !demandData.value || !productMasterData.value || !machineResourcesData.value;
});

const validateFile = (file) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;

            const lines = content.split('\n');
            const maxRows = 10;
            const maxCols = 10;
            if (lines.length > maxRows) {
                resolve(rt(tm('optimizeOpen.errorMaxRows', { maxRows })));
                return;
            }
            for (const line of lines) {
                const columns = line.split('\t');
                if (!line.includes('\t')) {
                    resolve(rt(tm('optimizeOpen.errorNotTsv')));
                    return;
                } else if (columns.length > maxCols) {
                    resolve(rt(tm('optimizeOpen.errorMaxColumns', { maxCols })));
                    return;
                }
            }
            resolve('');
        };
        reader.readAsText(file);
    });
};

const handleFileChange = (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    validateFile(file).then((errorMessage) => {
        if (type === 'demand') demandError.value = errorMessage;
        else if (type === 'productMaster') productMasterError.value = errorMessage;
        else if (type === 'machineResources') machineResourcesError.value = errorMessage;

        if (!errorMessage) {
            const reader = new FileReader();
            reader.onload = (e) => {
                if (type === 'demand') demandData.value = e.target.result;
                else if (type === 'productMaster') productMasterData.value = e.target.result;
                else if (type === 'machineResources') machineResourcesData.value = e.target.result;
            };
            reader.readAsText(file);
        }
    });
};

const analytics = getAnalytics();

const optimize = async () => {
    isCalculating.value = true;
    optimizationResult.value = null;

    logEvent(analytics, 'optimization_started', {
        timestamp: new Date().toISOString()
    });

    const formData = new FormData();
    formData.append('demand', demandData.value);
    formData.append('product_master', productMasterData.value);
    formData.append('machine_resources', machineResourcesData.value);

    try {
        const response = await fetch('https://us-central1-aligngengpt.cloudfunctions.net/https_flask_app/optimize_open', {
            //            const response = await fetch('http://localhost:5001/optimize_open', {
            method: 'POST',
            body: formData
        });
        const data = await response.json();
        optimizationResult.value = data;

        logEvent(analytics, 'optimization_completed', {
            status: 'success',
            AlgorithmId: 'open',
            timestamp: new Date().toISOString()
        });
    } catch (error) {
        console.error(rt(tm('optimizeOpen.error')), error);
        optimizationResult.value = { error: rt(tm('optimizeOpen.errorOccurred')) };

        logEvent(analytics, 'optimization_completed', {
            status: 'error',
            error_message: error.message,
            timestamp: new Date().toISOString()
        });
    } finally {
        isCalculating.value = false;
    }
};
</script>

<style scoped>
.optimize-open-content {
    width: 100%;
}

</style>